/**
 * Typed client for the agency-space routes (ADR-0237 Slice 1).
 *
 * One module for the three concerns the Slice 1 UI needs:
 *
 *   - GET  /v1/account/features                   — { agency, white_label }
 *   - GET  /v1/agency/clients                     — list clients (home session)
 *   - POST /v1/agency/clients/create              — path a (agency-created)
 *   - POST /v1/agency/clients/link-request        — path b (ask existing)
 *   - DELETE /v1/agency/clients/:link_id          — revoke own link
 *   - POST /v1/account/act-as/:client_account_id  — switch into a client
 *   - POST /v1/account/act-as/release             — return to home
 *   - GET  /v1/account/agency-access              — list agencies on this account
 *   - DELETE /v1/account/agency-access/:link_id   — client revokes agency
 *   - POST /v1/account/agency-access/accept       — client approves pending request
 *
 * Discriminated-union results mirror `account.ts` so the calling view
 * branches on `result.kind` rather than catching exceptions.
 */

import type {
  AccountFeaturesWire,
  ActingContextWire,
  AgencyAccessListItemWire,
  AgencyAccessPreviewWire,
  AgencyGrantedRole,
  ClientListItemWire,
} from '@sill/types';

import { AuthRateLimitError, AuthUnauthorizedError } from '@sill/types/api';
import { ApiError, type HttpClient } from './http-client.js';

export type FeaturesResult =
  | { readonly kind: 'ok'; readonly features: AccountFeaturesWire }
  | { readonly kind: 'error'; readonly message: string };

export async function getFeatures(http: HttpClient): Promise<FeaturesResult> {
  try {
    const res = await http.request<AccountFeaturesWire>({
      method: 'GET',
      path: '/v1/account/features',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', features: res };
  } catch (err) {
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type ListClientsResult =
  | { readonly kind: 'ok'; readonly clients: readonly ClientListItemWire[] }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function listClients(http: HttpClient): Promise<ListClientsResult> {
  try {
    const res = await http.request<readonly ClientListItemWire[]>({
      method: 'GET',
      path: '/v1/agency/clients',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', clients: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

// QA fix 2026-10-08 — pending outbound link requests for the Clients
// page. See GET /v1/agency/link-requests on the server.
export interface LinkRequestWire {
  readonly request_id: string;
  readonly target_email: string | null;
  readonly target_domain: string | null;
  readonly requested_role: string;
  readonly created_at: string;
  readonly expires_at: string;
}

export type ListLinkRequestsResult =
  | { readonly kind: 'ok'; readonly requests: readonly LinkRequestWire[] }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function listLinkRequests(http: HttpClient): Promise<ListLinkRequestsResult> {
  try {
    const res = await http.request<readonly LinkRequestWire[]>({
      method: 'GET',
      path: '/v1/agency/link-requests',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', requests: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export interface CreateClientBody {
  readonly name: string;
  readonly primary_domain: string;
  readonly owner_email: string;
  readonly invite_role: 'owner' | 'admin';
}

export interface CreateClientResponse {
  readonly client_account_id: string;
  readonly link_id: string;
  readonly owner_invite_sent: boolean;
}

export type CreateClientResult =
  | { readonly kind: 'ok'; readonly response: CreateClientResponse }
  | { readonly kind: 'owner_email_taken' }
  | { readonly kind: 'invalid'; readonly reason: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export async function createClient(
  http: HttpClient,
  body: CreateClientBody,
): Promise<CreateClientResult> {
  try {
    const res = await http.request<CreateClientResponse>({
      method: 'POST',
      path: '/v1/agency/clients/create',
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', response: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'conflict' && err.server_reason === 'owner_email_taken') {
        return { kind: 'owner_email_taken' };
      }
      if (err.kind === 'bad_request') {
        return { kind: 'invalid', reason: err.server_reason ?? 'invalid' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'rate_limited') {
        return { kind: 'rate_limited', message: explainApiError(err) };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export interface LinkRequestBody {
  readonly client_email?: string;
  readonly client_domain?: string;
  readonly requested_role: AgencyGrantedRole;
}

export interface LinkRequestResponse {
  readonly request_id: string;
  readonly expires_at: string;
}

export type LinkRequestResult =
  | { readonly kind: 'ok'; readonly response: LinkRequestResponse }
  | { readonly kind: 'invalid'; readonly reason: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export async function createLinkRequest(
  http: HttpClient,
  body: LinkRequestBody,
): Promise<LinkRequestResult> {
  try {
    const res = await http.request<LinkRequestResponse>({
      method: 'POST',
      path: '/v1/agency/clients/link-request',
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', response: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') {
        return { kind: 'invalid', reason: err.server_reason ?? 'invalid' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'rate_limited') {
        return { kind: 'rate_limited', message: explainApiError(err) };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type RevokeLinkResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function revokeAgencyLink(
  http: HttpClient,
  link_id: string,
): Promise<RevokeLinkResult> {
  try {
    await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/agency/clients/${encodeURIComponent(link_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * ADR-0240 Portal v3 (founder bug 2026-10-10) — Cancel a pending
 * client that the owner never claimed. Expires every outstanding
 * magic_link + revokes the link + marks the row cancelled. The
 * server refuses when the account was already claimed.
 */
export type CancelPendingClientResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'already_claimed' }
  | { readonly kind: 'not_pending' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function cancelPendingClient(
  http: HttpClient,
  client_account_id: string,
): Promise<CancelPendingClientResult> {
  try {
    await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/agency/clients/pending/${encodeURIComponent(client_account_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') {
        if (err.server_reason === 'already_claimed') return { kind: 'already_claimed' };
        if (err.server_reason === 'not_pending') return { kind: 'not_pending' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * ADR-0240 Portal v3 — resend the agency create-client invite with
 * a fresh magic_link. Burns one daily slot (same ledger as the
 * original invite). Server refuses on already-claimed / revoked
 * links.
 */
export type ResendPendingInviteResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'already_claimed' }
  | { readonly kind: 'not_pending' }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function resendPendingInvite(
  http: HttpClient,
  client_account_id: string,
): Promise<ResendPendingInviteResult> {
  try {
    await http.request<{ ok: true }>({
      method: 'POST',
      path: `/v1/agency/clients/pending/${encodeURIComponent(client_account_id)}/resend-invite`,
      body: {},
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof AuthRateLimitError) {
      return { kind: 'rate_limited', message: explainApiError(err) };
    }
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') {
        if (err.server_reason === 'already_claimed') return { kind: 'already_claimed' };
        if (err.server_reason === 'not_pending') return { kind: 'not_pending' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
      if (err.kind === 'rate_limited') {
        return { kind: 'rate_limited', message: explainApiError(err) };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * ADR-0240 Portal v3 — cancel an outbound link_request that the
 * client owner hasn't approved or declined yet. Server sets
 * `cancelled_at` so the accept token in the sent email is unusable.
 */
export type CancelLinkRequestResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'already_approved' }
  | { readonly kind: 'already_rejected' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function cancelLinkRequest(
  http: HttpClient,
  request_id: string,
): Promise<CancelLinkRequestResult> {
  try {
    await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/agency/link-requests/${encodeURIComponent(request_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict') {
        if (err.server_reason === 'already_approved') return { kind: 'already_approved' };
        if (err.server_reason === 'already_rejected') return { kind: 'already_rejected' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type ActAsResult =
  | { readonly kind: 'ok'; readonly context: ActingContextWire }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function actAsClient(
  http: HttpClient,
  client_account_id: string,
): Promise<ActAsResult> {
  try {
    const res = await http.request<ActingContextWire>({
      method: 'POST',
      path: `/v1/account/act-as/${encodeURIComponent(client_account_id)}`,
      // POST with no body — the server reads the client_account_id
      // from the URL and resolves the link row. Hono's JSON parser is
      // happy with an empty body.
      body: {},
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', context: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type ReleaseActingResult =
  | { readonly kind: 'ok'; readonly context: ActingContextWire }
  | { readonly kind: 'error'; readonly message: string };

export async function releaseActing(
  http: HttpClient,
): Promise<ReleaseActingResult> {
  try {
    const res = await http.request<ActingContextWire>({
      method: 'POST',
      path: '/v1/account/act-as/release',
      body: {},
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', context: res };
  } catch (err) {
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * Founder bug 2026-10-28: "Return to {agency}" failed once and still
 * reloaded, landing in the Agency Portal while acting. Release, retrying
 * once on a transient failure; callers navigate only on `ok`.
 */
export async function releaseActingWithRetry(
  http: HttpClient,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => { setTimeout(r, ms); }),
): Promise<ReleaseActingResult> {
  const first = await releaseActing(http);
  if (first.kind === 'ok') return first;
  await wait(800);
  return releaseActing(http);
}

/** Where "Return to {agency}" lands: the agency's own All sites screen. */
export const RELEASE_LANDING_PATH = '/?view=sites';

export type ListAgencyAccessResult =
  | { readonly kind: 'ok'; readonly agencies: readonly AgencyAccessListItemWire[] }
  | { readonly kind: 'error'; readonly message: string };

/** Shown when a non-owner member opens an agency approval link
 *  (only an owner may approve or decline — security review 2026-10-11). */
export const OWNER_ONLY_APPROVAL_COPY =
  'Only an owner of this account can approve or decline agency access. Ask an owner to open this link.';

export async function listAgencyAccess(
  http: HttpClient,
): Promise<ListAgencyAccessResult> {
  try {
    const res = await http.request<readonly AgencyAccessListItemWire[]>({
      method: 'GET',
      path: '/v1/account/agency-access',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', agencies: res };
  } catch (err) {
    return { kind: 'error', message: explainApiError(err) };
  }
}

export async function revokeAgencyAccess(
  http: HttpClient,
  link_id: string,
): Promise<RevokeLinkResult> {
  try {
    await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/account/agency-access/${encodeURIComponent(link_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type AcceptAgencyAccessResult =
  | { readonly kind: 'ok'; readonly link_id: string }
  | { readonly kind: 'invalid_or_expired' }
  | { readonly kind: 'error'; readonly message: string };

export async function acceptAgencyAccess(
  http: HttpClient,
  approval_token: string,
): Promise<AcceptAgencyAccessResult> {
  try {
    const res = await http.request<{ link_id: string }>({
      method: 'POST',
      path: '/v1/account/agency-access/accept',
      body: { approval_token },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', link_id: res.link_id };
  } catch (err) {
    // Server collapses every failure (bad token, expired, consumed,
    // wrong account) to 401 invalid_or_expired — mirror that uniform
    // failure in the client so the UI copy stays one line.
    if (isUnauthorized(err)) {
      return { kind: 'invalid_or_expired' };
    }
    if (err instanceof ApiError && err.server_reason === 'role_insufficient') {
      return { kind: 'error', message: OWNER_ONLY_APPROVAL_COPY };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type PreviewAgencyAccessResult =
  | { readonly kind: 'ok'; readonly preview: AgencyAccessPreviewWire }
  | { readonly kind: 'invalid_or_expired' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * ADR-0237 — read-only preview of a pending link-request's agency
 * display name + requested role + expiry. The server never consumes
 * the token on this call; every failure is uniform 401.
 */
export async function previewAgencyAccess(
  http: HttpClient,
  approval_token: string,
): Promise<PreviewAgencyAccessResult> {
  try {
    const res = await http.request<AgencyAccessPreviewWire>({
      method: 'POST',
      path: '/v1/account/agency-access/preview',
      body: { approval_token },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', preview: res };
  } catch (err) {
    if (isUnauthorized(err)) {
      return { kind: 'invalid_or_expired' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type DeclineAgencyAccessResult =
  | { readonly kind: 'ok'; readonly request_id: string }
  | { readonly kind: 'invalid_or_expired' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * ADR-0237 — decline a pending link-request. Mirrors `acceptAgencyAccess`
 * byte-for-byte at the HTTP layer; the server sets `rejected_at`
 * instead of minting a link. Single-use; every failure is uniform 401.
 */
export async function declineAgencyAccess(
  http: HttpClient,
  approval_token: string,
): Promise<DeclineAgencyAccessResult> {
  try {
    const res = await http.request<{ request_id: string }>({
      method: 'POST',
      path: '/v1/account/agency-access/decline',
      body: { approval_token },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', request_id: res.request_id };
  } catch (err) {
    if (isUnauthorized(err)) {
      return { kind: 'invalid_or_expired' };
    }
    if (err instanceof ApiError && err.server_reason === 'role_insufficient') {
      return { kind: 'error', message: OWNER_ONLY_APPROVAL_COPY };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * The HttpClient throws AuthUnauthorizedError (not ApiError) on a 401, so
 * the approval routes' uniform 401 was falling through to the raw word
 * "unauthorized" (founder bug 2026-10-28).
 */
function isUnauthorized(err: unknown): boolean {
  return err instanceof AuthUnauthorizedError || (err instanceof ApiError && err.status === 401);
}

function explainApiError(err: unknown): string {
  if (err instanceof AuthUnauthorizedError) {
    return 'Your session has ended. Sign in again.';
  }
  // The HttpClient throws AuthRateLimitError (not ApiError) on every 429;
  // the agency invite caps put their code in the body `reason`.
  if (err instanceof AuthRateLimitError) {
    if (err.server_reason === 'agency_daily_limit_reached') {
      const hours = Math.max(1, Math.ceil(err.retry_after_minutes / 60));
      return `You've reached today's limit for client invites and access requests. It resets in about ${hours} ${hours === 1 ? 'hour' : 'hours'}. Contact Sill to raise it.`;
    }
    return 'Too many requests in a short time. Wait a minute and try again.';
  }
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'service_unavailable':
        return 'Agency service is temporarily unavailable.';
      case 'network_timeout':
        return 'This may have saved — refresh to check before trying again.';
      case 'network_error':
        return 'Network error — check your connection.';
      case 'bad_response':
        return 'Server returned an unexpected response.';
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Network error.';
}
